import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  FaLaptopCode, FaMobileAlt, FaCogs, FaCloud, 
  FaChartLine, FaPlug 
} from 'react-icons/fa';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      icon: <FaLaptopCode />,
      title: "Website Development",
      description: "Responsive, secure websites using React.js, Laravel, PHP",
      color: "#3B82F6"
    },
    {
      icon: <FaMobileAlt />,
      title: "Mobile App Development",
      description: "Flutter-based apps for Android/iOS with offline support",
      color: "#8B5CF6"
    },
    {
      icon: <FaCogs />,
      title: "Custom Systems",
      description: "ERP modules, attendance systems, complaint portals",
      color: "#10B981"
    },
    {
      icon: <FaCloud />,
      title: "SaaS Development",
      description: "Full-stack SaaS platforms with cloud backend",
      color: "#F59E0B"
    },
    {
      icon: <FaChartLine />,
      title: "Admin Dashboards",
      description: "Custom reporting, analytics dashboards",
      color: "#EF4444"
    },
    {
      icon: <FaPlug />,
      title: "API Integration",
      description: "Firebase, Google APIs, Payment, Auth, Messaging",
      color: "#06B6D4"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="services" className="section-padding">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: -30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
            transition={{ duration: 0.6 }}
          >
            <motion.h2 
              className="section-title"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
            >
              Services Offered
            </motion.h2>
            <motion.div 
              className="section-divider"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            ></motion.div>
            <motion.p 
              className="section-description"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.6 }}
            >
              Comprehensive solutions for your digital needs
            </motion.p>
          </motion.div>

          <motion.div
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={cardVariants}
                initial={{ scale: 0, rotateY: -180 }}
                animate={isInView ? { scale: 1, rotateY: 0 } : { scale: 0, rotateY: -180 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: index * 0.15
                }}
                whileHover={{ 
                  y: -15,
                  scale: 1.05,
                  rotateX: 5,
                  rotateY: 5,
                  transition: { 
                    type: "spring",
                    stiffness: 300,
                    damping: 20
                  }
                }}
              >
                <motion.div 
                  className="service-icon-wrapper"
                  style={{ backgroundColor: `${service.color}15` }}
                  whileHover={{ 
                    rotate: 360,
                    scale: 1.2,
                    transition: { duration: 0.6 }
                  }}
                  animate={{
                    scale: [1, 1.05, 1],
                    transition: {
                      repeat: Infinity,
                      duration: 2,
                      delay: index * 0.2
                    }
                  }}
                >
                  <span 
                    className="service-icon"
                    style={{ color: service.color }}
                  >
                    {service.icon}
                  </span>
                </motion.div>
                <motion.h3 
                  className="service-title"
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ delay: index * 0.15 + 0.3 }}
                >
                  {service.title}
                </motion.h3>
                <motion.p 
                  className="service-description"
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ delay: index * 0.15 + 0.4 }}
                >
                  {service.description}
                </motion.p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
